
import React, { useState } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';
import ParticipantAvatar from './participant/ParticipantAvatar';
import RemoteAudioPlayer from './RemoteAudioPlayer';
import ParticipantVideo from './participant/ParticipantVideo';
import ParticipantScreenShare from './participant/ParticipantScreenShare';
import ParticipantAudioIndicator from './participant/ParticipantAudioIndicator';
import ParticipantStatusIndicators from './participant/ParticipantStatusIndicators';
import ParticipantNameTag from './participant/ParticipantNameTag';
import ParticipantLocalBadge from './participant/ParticipantLocalBadge';

interface LiveSessionParticipantProps {
  name: string;
  role: string;
  avatarUrl: string;
  isLocal?: boolean;
  isMuted?: boolean;
  isVideoOff?: boolean;
  isScreenSharing?: boolean;
  isEnlarged?: boolean;
  onToggleEnlarge?: () => void;
  className?: string;
  screenStream?: MediaStream | null;
  localVideoRef?: React.RefObject<HTMLVideoElement>;
  localStream?: MediaStream | null;
  remoteStream?: MediaStream;
  audioLevel?: number;
}

const LiveSessionParticipant: React.FC<LiveSessionParticipantProps> = ({
  name,
  role,
  avatarUrl,
  isLocal = false,
  isMuted = false,
  isVideoOff = false,
  isScreenSharing = false,
  isEnlarged = false,
  onToggleEnlarge = () => {},
  className = '',
  screenStream = null,
  localVideoRef,
  localStream,
  remoteStream,
  audioLevel = 0
}) => {
  const [hasAudioActivity, setHasAudioActivity] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const isMobile = useIsMobile();
  
  const minHeight = isEnlarged ? 'min-h-[300px]' : 'min-h-[150px]';
  
  return (
    <div 
      className={`relative rounded-lg overflow-hidden bg-gradient-to-b from-slate-800 to-slate-900 flex flex-col items-center justify-center h-full ${minHeight} ${className}`}
      onClick={onToggleEnlarge}
    >
      {/* When video is off, show avatar */}
      {isVideoOff && (
        <div className="absolute inset-0 bg-gray-900 flex items-center justify-center z-10">
          <ParticipantAvatar name={name} avatarUrl={avatarUrl} />
        </div>
      )}
      
      {/* Video component */}
      <ParticipantVideo 
        isLocal={isLocal} 
        isVideoOff={isVideoOff} 
        localVideoRef={localVideoRef} 
        localStream={localStream} 
      />
      
      {/* Screen sharing */}
      <ParticipantScreenShare 
        isScreenSharing={isScreenSharing} 
        isLocal={isLocal} 
        name={name} 
        screenStream={screenStream} 
      />
      
      {/* Camera error message */}
      {cameraError && isLocal && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white text-sm p-4 text-center z-30">
          <p>{cameraError}</p>
        </div>
      )}
      
      {/* Audio visualization indicator */}
      <ParticipantAudioIndicator isMuted={isMuted} audioLevel={audioLevel} />
      
      {/* Remote audio playback */}
      {remoteStream && !isLocal && (
        <RemoteAudioPlayer 
          stream={remoteStream} 
          peerId={name}
          onAudioLevel={(level) => {
            // Could update visual feedback here
          }}
        />
      )}
      
      {/* Status icons */}
      <ParticipantStatusIndicators 
        isMuted={isMuted} 
        isVideoOff={isVideoOff} 
        isScreenSharing={isScreenSharing} 
      />
      
      {/* "You" badge */}
      <ParticipantLocalBadge isLocal={isLocal} />
      
      {/* Name and role tag */}
      <ParticipantNameTag name={name} role={role} isMuted={isMuted} />
    </div>
  );
};

export default LiveSessionParticipant;
